/* eslint-disable complexity, react/forbid-component-props, sonarjs/cognitive-complexity, sonarjs/no-identical-functions */
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import propTypes from 'prop-types';
import { useContext, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Cookies from 'react-cookies';
import { useSelector } from 'react-redux';
import COOKIES from '../../config/cookies/cookies';
import {
  BRANCH_LINKS_DEV,
  BRANCH_LINKS_PROD,
  LINK_CHECKOUT_BUTTON,
} from '../../config/links/links';
import { BRAND_WOWCHER, GIFTING_OPTIONS } from '../../config/setup/setup';
import {
  BUTTON_BUY_IT_AGAIN,
  GIFTED,
  GIFT_IT,
  GIFT_PACK,
  GIFT_PACK_SENT,
  HOW_TO_REDEEM_WOWCHER,
  REDEEM_IN_APP,
  VIEW_PROGRESS,
} from '../../config/text/text';
import { trackEvent } from '../../helpers/analytics';
import { basketCreator, mergeProducts } from '../../helpers/basketCreator';
import useCheckMobile from '../../helpers/checkMobile';
import errorTrackingService from '../../helpers/errorTrackingService';
import { isWowcherBrand } from '../../helpers/pages';
import { redirectToURL } from '../../helpers/url';
import { getIsEligibleForVip } from '../../helpers/vipSubscribeHelper';
import {
  showInlineGetButton,
  showRedeem,
  showViewProgress,
  showNeedHelp,
  showGiftOptions,
  isGiftPack,
} from '../../helpers/vouchers';
import ThemeContext from '../../providers/ThemeProvider';
import { createBasket, fetchBasket } from '../../redux/actions/basket';
import { replaceBasket } from '../../services/basket';
import RedeemVoucherInfoModal from '../myaccount/vouchers/RedeemVoucherInfoModal';
import { GetVoucherButton } from './GetVoucherButton';
import VoucherAccordionButton from './VoucherAccordionButton';
import VoucherAccordionGifting from './VoucherAccordionGifting';
import VoucherAccordionHelp from './VoucherAccordionHelp';
import VoucherAccordionProgress from './VoucherAccordionProgress';
import VoucherAccordionRedeem from './VoucherAccordionRedeem';
import VoucherNeedHelpButton from './VoucherNeedHelpButton';
import {
  DEFAULT_EVENT_KEY,
  GIFTING_EVENT_KEY,
  HELP_EVENT_KEY,
  PROGRESS_EVENT_KEY,
  REDEEM_EVENT_KEY,
} from './constants';

const VoucherAccordion = ({
  voucher,
  toggleState,
  setToggle,
  mutate,
  isGiftClaimable,
}) => {
  const theme = useContext(ThemeContext);
  const isMobile = useCheckMobile(false);
  const [showModal, setShowModal] = useState(false);
  const router = useRouter();
  const onModal = (event) => {
    if (event.key && event.key === 'Enter') {
      setShowModal(true);
    }
  };
  const isProduction = process.env.NEXT_PUBLIC_ENVIRONMENT !== 'dev';
  const redirectURL = isProduction
    ? BRANCH_LINKS_PROD[process.env.NEXT_PUBLIC_SITE]
    : BRANCH_LINKS_DEV[process.env.NEXT_PUBLIC_SITE];
  const isGiftPacked = isGiftPack(voucher.giftType);
  const isExperienceBox = Boolean(voucher.experienceGiftBox);
  const locationStore = useSelector((state) => state.locations);
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const userVipStatus = useSelector(
    (state) => state.user.userprofile?.userVipStatus,
  );

  const setToggleState = () => {
    toggleState === DEFAULT_EVENT_KEY && trackEvent('myaccount-needhelp');
    setToggle(
      toggleState === HELP_EVENT_KEY ? DEFAULT_EVENT_KEY : HELP_EVENT_KEY,
    );
  };

  const buyItAgainClicked = async () => {
    trackEvent('buy_again_click_my_wowchers');
    if (voucher?.buyAgainRedirectToDealPage) {
      return redirectToURL(`${voucher.dealUrl}`, router);
    }

    const deal = {
      adId: null,
      bookingCalendarOnRedemption:
        voucher?.bookingCalendarOnRedemption || false,
      checkInDate: voucher?.checkInDate || null,
      checkOutDate: voucher?.checkOutDate || null,
      dealId: voucher?.dealId,
      dealProductId: voucher?.buyAgainProductId,
      dealType: voucher?.productDisplay?.type || 'default',
      depositDisplayPrice: voucher?.depositProduct?.price || null,
      depositProduct: voucher?.depositProduct || null,
      depositProductId: voucher?.depositProduct?.id || null,
      discount: voucher?.buyAgainDiscount,
      discountPercentage: voucher?.buyAgainDiscountPercentage,
      discountValue: voucher?.discount,
      displayDiscount: voucher?.showDiscount,
      flights: voucher?.flights || null,
      gift: voucher?.gift,
      giftClaim: voucher?.giftClaim || false,
      gifting:
        voucher.giftType === GIFTING_OPTIONS.E_GIFT
          ? {
              gift: true,
              giftPackProductId: null,
              giftProductId: null,
              type: 'e-gift',
            }
          : null,
      hasDeposit: voucher?.hasDeposit,
      hideProductDiscount: !voucher?.showDiscount,
      id: voucher?.dealId,
      originalPrice: voucher?.originalPrice,
      payDeposit: false,
      postagePrice: voucher?.buyAgainPostagePrice,
      price: voucher?.buyAgainPrice,
      products: [voucher],
      purchaseCap: voucher?.purchaseCap,
      purchaseGclid: Cookies.load(COOKIES.gclid) || null,
      purchaseLocation: locationStore?.location?.shortName,
      quantity: 1,
      sessionPurchaseSource: Cookies.load(COOKIES.sessionSource) || null,
      title: voucher?.title,
    };

    voucher.id = voucher.buyAgainProductId;

    const basketToken = Cookies.load(COOKIES.basketToken);
    const customerToken = Cookies.load(COOKIES.customerToken);
    const currentBasket = await fetchBasket(
      basketToken,
      customerToken,
      isAuthenticated,
    );

    const products = basketCreator(deal, locationStore, 0, false, false);
    const mergedProducts = currentBasket?.products
      ? mergeProducts(currentBasket.products, products)
      : products;

    const isExistingBasket = basketToken && basketToken.length > 10;
    const basketTokenAux = isExistingBasket
      ? await replaceBasket(
          basketToken,
          mergedProducts,
          isAuthenticated,
          getIsEligibleForVip(isAuthenticated, userVipStatus),
        )
      : await createBasket(
          mergedProducts,
          getIsEligibleForVip(isAuthenticated, userVipStatus),
        );

    if (!basketTokenAux && !basketToken) {
      return errorTrackingService.logError({
        error: 'No Basket Token',
        message: 'Basket token cannot be undefined.',
      });
    }
    redirectToURL(
      `${LINK_CHECKOUT_BUTTON}/${basketToken || basketTokenAux}`,
      router,
    );
  };

  return (
    <>
      <Accordion activeKey={toggleState} className="voucher-toggle-container">
        {!isExperienceBox && (
          <VoucherAccordionRedeem
            setToggle={setToggle}
            toggleState={toggleState}
            voucher={voucher}
          />
        )}
        <VoucherAccordionProgress
          setToggle={setToggle}
          toggleState={toggleState}
          voucher={voucher}
        />
        <VoucherAccordionHelp setToggle={setToggle} voucher={voucher} />
        <VoucherAccordionGifting
          mutate={mutate}
          setToggle={setToggle}
          voucher={voucher}
        />
      </Accordion>
      {showRedeem(voucher) &&
        isMobile &&
        !isExperienceBox &&
        !voucher.hideRedeemInfo && (
          <div className="redeem-info">
            <FontAwesomeIcon
              aria-label={HOW_TO_REDEEM_WOWCHER}
              className="redeem__info"
              icon={faInfoCircle}
              onClick={() => setShowModal(true)}
              onKeyDown={onModal}
              role="button"
              tabIndex={0}
            />
            <RedeemVoucherInfoModal
              onClose={() => setShowModal(false)}
              setToggle={setToggle}
              show={showModal}
              toggleState={toggleState}
              voucher={voucher}
            />
            <p className="redeem__info-text">{' ' + HOW_TO_REDEEM_WOWCHER}</p>
          </div>
        )}
      <div className="buttons">
        {showRedeem(voucher) &&
          isMobile &&
          !isExperienceBox &&
          !voucher.hideRedeemInfo && (
            <VoucherAccordionButton
              className={classNames({ 'grey-button': voucher.gifted })}
              isToggled={toggleState === REDEEM_EVENT_KEY}
              onClick={() => {
                redirectToURL(redirectURL, router);
              }}
            >
              {REDEEM_IN_APP}
            </VoucherAccordionButton>
          )}
        {showInlineGetButton(voucher) && <GetVoucherButton voucher={voucher} />}
        {showViewProgress(voucher) && !voucher.hideRedeemInfo && (
          <VoucherAccordionButton
            className="green-button"
            isToggled={toggleState === PROGRESS_EVENT_KEY}
            onClick={() => {
              trackEvent('view_progress_button', {
                eVar49: voucher.dealId,
                pageName: 'my account: my vouchers: view order progress',
                url: window.url,
              });
              setToggle(
                toggleState === PROGRESS_EVENT_KEY
                  ? DEFAULT_EVENT_KEY
                  : PROGRESS_EVENT_KEY,
              );
            }}
          >
            {VIEW_PROGRESS}
          </VoucherAccordionButton>
        )}
        {showGiftOptions(voucher) && !isGiftClaimable && (
          <>
            {!isGiftPacked && toggleState === DEFAULT_EVENT_KEY && (
              <VoucherAccordionButton
                className={classNames({ 'grey-button': voucher.gifted })}
                disabled={isGiftPacked}
                isToggled={toggleState === GIFTING_EVENT_KEY && !isGiftPacked}
                onClick={() => {
                  setToggle(
                    toggleState === GIFTING_EVENT_KEY
                      ? DEFAULT_EVENT_KEY
                      : GIFTING_EVENT_KEY,
                  );
                }}
              >
                {voucher.gifted ? GIFTED : GIFT_IT}
              </VoucherAccordionButton>
            )}
            {isGiftPacked && toggleState === DEFAULT_EVENT_KEY && (
              <VoucherAccordionButton
                className={classNames({ 'grey-button': voucher.gifted })}
                disabled={!isGiftPacked}
                isToggled={toggleState === GIFTING_EVENT_KEY && isGiftPacked}
                onClick={() => {
                  setToggle(
                    toggleState === GIFTING_EVENT_KEY
                      ? DEFAULT_EVENT_KEY
                      : GIFTING_EVENT_KEY,
                  );
                }}
              >
                {voucher.gifted ? GIFT_PACK_SENT : GIFT_PACK}
              </VoucherAccordionButton>
            )}
          </>
        )}
        {voucher.showBuyAgain && (
          <VoucherAccordionButton
            className="secondary-button"
            onClick={buyItAgainClicked}
          >
            {BUTTON_BUY_IT_AGAIN}
          </VoucherAccordionButton>
        )}
        {showNeedHelp(voucher) && (
          <VoucherNeedHelpButton
            onClick={setToggleState}
            toggleState={toggleState}
          />
        )}
      </div>
      <style jsx>{`
        .buttons {
          display: flex;
          flex-direction: column;
        }
        .buttons :global(.general-button) {
          text-transform: ${isWowcherBrand() ? 'uppercase' : 'lowercase'};
          width: 100%;
        }
        .redeem__info-text {
          color: ${theme.colors.primary};
          display: inline;
        }
        .redeem-info {
          padding-bottom: 10px;
        }
        @media (min-width: ${theme.breakpoints.lgUp}) {
          .buttons {
            flex-direction: row;
            flex-wrap: wrap;
          }
          .buttons :global(.general-button) {
            margin-left: 20px;
            width: calc(100% / 3 - (40px / 3));
          }
          .buttons :global(.general-button:first-child) {
            margin: 0px auto 0px 0px;
          }
          .buttons :global(.general-button:nth-child(4n)) {
            margin: 0 auto;
          }
          .green-text {
            padding-top: 10px;
            color: #abdb78;
            text-align: center;
          }
          .green-text-link {
            color: #abdb78;
            text-decoration: underline;
          }
        }
      `}</style>
    </>
  );
};

VoucherAccordion.propTypes = {
  isGiftClaimable: propTypes.bool,
  mutate: propTypes.func,
  setToggle: propTypes.func.isRequired,
  toggleState: propTypes.string.isRequired,
  voucher: propTypes.object,
};

export default VoucherAccordion;
